import React, { useState, useEffect } from 'react';
import { URLPRIFIX } from '../../functions/api';
import Pagination from '../Fields/Pagination';

const ListViewUp = ({
  header,
  filteredRecords,
  Tablehead,
  Tablerecord,
  sortConfig,
  handleSort,
  filters,
  onFilterChange,
  store,
  handleOnSelect,
  isSelected,
  handleClickRow,
  handleDelete,
  handleSelectAll,
  isAllSelected,
  tablename,
  currentPage = 1,
  pageSize = 20,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchField, setActiveSearchField] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");

  // Enabled globally for all transaction and listing screens
  const isAccountingModule = true;

  // Keep local search query in sync with parent search variables if cleared externally
  const storeSearch = store?.getvalue("search") || store?.getvalue("tasksearch") || "";
  useEffect(() => {
    if (!storeSearch) {
      setSearchQuery("");
    }
  }, [storeSearch]);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const getStatusTagClass = (status) => {
    const baseClasses = "inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap";

    switch (status?.toLowerCase()) {
  case "completed":
    return `${baseClasses} bg-[#f4ecf7] text-[#212f3d] border border-[#D7BDE2]`;

  case "created":
    return `${baseClasses} bg-[#f4ecf7] text-[#212f3d] border border-[#C39BD3]`;

  case "billed":
    return `${baseClasses} bg-[#E6F4EA] text-[#188038] border border-[#81C995]`;

  case "paid":
    return `${baseClasses} bg-[#E6F4EA] text-[#188038] border border-[#81C995]`;

  case "paid with tds":
    return `${baseClasses} bg-[#E6F4EA] text-[#188038] border border-[#81C995]`;

  case "awaiting tds":
    return `${baseClasses} bg-[#FFF8E1] text-[#B26A00] border border-[#F6C453]`;

  case "inprogress":
    return `${baseClasses} bg-[#F9E79F] text-[#212F3D] border border-[#F4D03F]`;

  case "cancelled":
    return `${baseClasses} bg-[#F8D7DA] text-[#721C24] border border-[#E57373]`;

  case "draft":
    return `${baseClasses} bg-[#D6EAF8] text-[#21618C] border border-[#85C1E9]`;

  case "waitingforapproval":
    return `${baseClasses} bg-[#FFEBAA] text-[#664D03] border border-[#F5B041]`;

  case "awaiting approval":
    return `${baseClasses} bg-[#FFEBAA] text-[#664D03] border border-[#F5B041]`;

  case "approved":
    return `${baseClasses} bg-[#E6F4EA] text-[#188038] border border-[#34A853]`;

  case "rejected":
    return `${baseClasses} bg-[#FFCCCB] text-[#CC0000] border border-[#EF5350]`;

  case "hold":
    return `${baseClasses} bg-[#E59866] text-[#212F3D] border border-[#CA6F1E]`;

  default:
    return `${baseClasses} bg-slate-50 text-slate-600 border border-[#CBD5E1]`;
}
  };

  // --- DYNAMICALLY ADD CREATED DATE TO THE HEADER ARRAY ONLY FOR ACCOUNTING ---
  const hasCreatedDate = header.some(([_, label]) => label === "createdDate" || label === "created_date" || label === "createddate");
  const finalHeader = (isAccountingModule && !hasCreatedDate)
    ? [
        ...header.filter(([_, label]) => label !== "ACTION"),
        ["Created Date", "createdDate"],
        ...header.filter(([_, label]) => label === "ACTION"),
      ]
    : header;

  // Extract available years dynamically from the dataset strictly based on Created Date (for Accounting)
  const years = Array.from(
    new Set(
      filteredRecords
        .map((r) => {
          const d = r.createdDate || r.createddate || r.created_date || r.createdOn || r.createdon || r.date;
          if (d) {
            const parsed = new Date(d);
            if (!isNaN(parsed.getTime())) return parsed.getFullYear();
          }
          return null;
        })
        .filter((y) => y !== null)
    )
  ).sort((a, b) => b - a);

  const availableYears = years.length > 0 ? years : [2024, 2025, 2026, 2027];

  // --- COMPACT DOUBLE FILTERING ALGORITHM ---
  // Filter the already tab-filtered records locally using the search bar input query and month/year selector.
  const doubleFilteredRecords = filteredRecords.filter((record) => {
    // 1. Month-Year Filtering based strictly on Created Date (Only in Accounting Module)
    if (isAccountingModule && (selectedMonth !== "all" || selectedYear !== "all")) {
      const dateVal = record.createdDate || record.createddate || record.created_date || record.createdOn || record.createdon || record.date;
      if (!dateVal) return false;
      const parsedDate = new Date(dateVal);
      if (isNaN(parsedDate.getTime())) return false;

      if (selectedMonth !== "all" && parsedDate.getMonth().toString() !== selectedMonth) {
        return false;
      }
      if (selectedYear !== "all" && parsedDate.getFullYear().toString() !== selectedYear) {
        return false;
      }
    }

    // 2. Search Query Filtering
    if (!searchQuery) return true;
    const term = searchQuery.toLowerCase().trim();

    if (activeSearchField === "all") {
      // Search across all valid header fields
      return finalHeader.some(([_, label]) => {
        if (label === "ACTION" || label === "id" || label === "select") return false;
        // Map dynamic createdDate created_date values if querying that field
        const val = (label === "createdDate" || label === "created_date" || label === "createddate")
          ? (record.createdDate || record.createddate || record.created_date || record.createdOn || record.createdon || record.date)
          : record[label];
        return val && val.toString().toLowerCase().includes(term);
      });
    } else {
      // Search in specific selected field
      const val = (activeSearchField === "createdDate" || activeSearchField === "created_date" || activeSearchField === "createddate")
        ? (record.createdDate || record.createddate || record.created_date || record.createdOn || record.createdon || record.date)
        : record[activeSearchField];
      return val && val.toString().toLowerCase().includes(term);
    }
  });

  const totalRecordsCount = store?.getvalue("totalrecords") || filteredRecords.length;
  const displayedCount = doubleFilteredRecords.length;

  const globalPageCallback = store?.getsystemvalue("pagecallback");
  const wrappedCallback = (page, size) => {
    if (globalPageCallback) {
      globalPageCallback(page, size);
    }
  };
  wrappedCallback.isGlobalWrapper = true;

  return (
    <div className="flex flex-col w-full gap-5">
      {/* Premium Top-Level Search & Filter Panel */}
      <div className="p-5 rounded-2xl border border-slate-100 bg-[#fcfcfe] flex flex-col gap-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3.5">
          {/* Elegant Search Input */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="#6366F1" className="w-4.5 h-4.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.637 10.637z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${tablename || 'records'}...`}
              className="block w-full pl-10 pr-10 py-2.5 bg-white border rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0', fontFamily: 'Inter, sans-serif' }}
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            )}
          </div>

          {/* Dynamic Month Selector (Only for Accounting Module) */}
          {isAccountingModule && (
            <div className="flex gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300 min-w-[140px]"
                style={{ borderColor: '#e2e8f0' }}
              >
                <option value="all">All Months</option>
                <option value="0">January</option>
                <option value="1">February</option>
                <option value="2">March</option>
                <option value="3">April</option>
                <option value="4">May</option>
                <option value="5">June</option>
                <option value="6">July</option>
                <option value="7">August</option>
                <option value="8">September</option>
                <option value="9">October</option>
                <option value="10">November</option>
                <option value="11">December</option>
              </select>
            </div>
          )}

          {/* Dynamic Year Selector (Only for Accounting Module) */}
          {isAccountingModule && (
            <div className="flex gap-2">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300 min-w-[120px]"
                style={{ borderColor: '#e2e8f0' }}
              >
                <option value="all">All Years</option>
                {availableYears.map((yr) => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
            </div>
          )}

          {/* Dynamic Field Selector */}
          <div className="flex gap-2">
            <select
              value={activeSearchField}
              onChange={(e) => setActiveSearchField(e.target.value)}
              className="bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300 min-w-[160px]"
              style={{ borderColor: '#e2e8f0' }}
            >
              <option value="all">Search All Fields</option>
              {finalHeader
                .filter(([name, label]) => label !== "ACTION" && label !== "id" && label !== "select")
                .map(([name, label], idx) => (
                  <option key={idx} value={label}>{name}</option>
                ))
              }
            </select>
          </div>
        </div>

        {/* Dynamic Pills & Top Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mr-1">Status:</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', border: '1px solid #E0E7FF' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] animate-pulse"></span>
              Tab Count ({totalRecordsCount})
            </span>
            {isAccountingModule && selectedMonth !== "all" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', border: '1px solid #E0E7FF' }}>
                Month: {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][selectedMonth]}
              </span>
            )}
            {isAccountingModule && selectedYear !== "all" && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', border: '1px solid #E0E7FF' }}>
                Year: {selectedYear}
              </span>
            )}
            {(searchQuery !== "" || (isAccountingModule && (selectedMonth !== "all" || selectedYear !== "all"))) && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D' }}>
                Query Match: {displayedCount} records
              </span>
            )}
          </div>
          {globalPageCallback && (
            <div className="scale-90 origin-right">
              <Pagination onchange={wrappedCallback} />
            </div>
          )}
        </div>
      </div>

      {/* Main Table Display */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse" style={{ minWidth: '1000px' }}>
          <thead>
            {/* Clean Light High-Visibility Header */}
            <tr style={{ backgroundColor: '#EEF2FF', borderBottom: '2px solid #E2E8F0' }}>
              <th className="pl-3 py-2.5 w-6 text-left whitespace-nowrap">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={isAllSelected}
                  className="rounded border-slate-300 h-4 w-4 cursor-pointer accent-[#6366F1]"
                />
              </th>
              <th className="px-1 py-2.5 w-8 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center whitespace-nowrap">
                S.No.
              </th>
              {finalHeader.map(([name, label], index) => {
                if (label !== "id" && label !== "ACTION" && label !== "select") {
                  const isSorted = sortConfig.field === label;
                  const isDateHeader = name.toLowerCase().includes("date") || name.toLowerCase().includes("created");
                  return (
                    <th
                      key={index}
                      onClick={() => handleSort(label)}
                      className={`px-3 py-2.5 text-[10px] font-black uppercase tracking-widest cursor-pointer group text-slate-600 hover:text-[#4F46E5] ${isDateHeader ? 'whitespace-nowrap' : ''}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{name}</span>
                        {isSorted && (
                          <span className="font-extrabold text-[#4F46E5]">
                            {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                } else if (
                  label === "ACTION" &&
                  (store.getprops("update") || store.getprops("view") || store.getprops("delete"))
                ) {
                  return (
                    <th key={index} className="px-3 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-600 text-right pr-4 whitespace-nowrap">
                      <span>{name}</span>
                    </th>
                  );
                }
                return null;
              })}
            </tr>
          </thead>
          <tbody className="">
            {doubleFilteredRecords.map((item, index) => {
              const selected = isSelected(item);
              return (
                <tr
                  key={item.id || index}
                  className="hover:bg-slate-50/80 transition-all duration-150 cursor-pointer"
                  style={{ 
                    backgroundColor: selected ? '#EEF2FF' : 'transparent',
                    borderBottom: '2px solid #E2E8F0'
                  }}
                >
                  <td className="pl-3 py-2 w-6 text-left" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      onChange={(event) => {
                        handleOnSelect(item, event.target.checked);
                      }}
                      checked={selected}
                      className="rounded border-slate-300 h-4 w-4 cursor-pointer accent-[#6366F1]"
                    />
                  </td>
                  <td className="px-1 py-2 text-xs font-normal text-slate-400 text-center w-8">
                    <span className="inline-flex items-center justify-center bg-slate-100 text-slate-600 font-normal text-xs px-1.5 py-0.5 rounded">
                      {(currentPage - 1) * pageSize + index + 1}
                    </span>
                  </td>

                  {finalHeader.map(([name, label], fieldindex) => {
                    if (label === "ACTION") {
                      if (store.getprops("update") || store.getprops("delete")) {
                        return (
                          <td key={fieldindex} className="px-3 py-2 text-right pr-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-3.5">
                              {store.getprops("update") && (
                                <button
                                  onClick={() => {
                                    store.navTo(`/${URLPRIFIX}/${tablename}/edit/${item.id}`);
                                  }}
                                  className="text-slate-400 hover:text-[#4F46E5] p-1.5 hover:bg-indigo-50 rounded-lg transition-all duration-150 cursor-pointer border-0"
                                  title="Edit"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.83 20.013a4.5 4.5 0 01-1.882 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.881L16.862 4.487z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125l-3-3" />
                                  </svg>
                                </button>
                              )}
                              {store.getprops("delete") && (
                                <button
                                  onClick={() => handleDelete(item)}
                                  className="text-slate-400 hover:text-rose-600 p-1.5 hover:bg-rose-50 rounded-lg transition-all duration-150 cursor-pointer border-0"
                                  title="Delete"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      }
                    } else if (label === "activeStatus") {
                      return (
                        <td key={fieldindex} className="px-3 py-2 text-xs font-normal text-slate-700 whitespace-nowrap" onClick={() => handleClickRow && handleClickRow(item)}>
                          {item[label] ? "Active" : "Inactive"}
                        </td>
                      );
                    } else if (label === "status" || label === "expense_status" || label === "invoice_status") {
                      return (
                        <td
                          key={fieldindex}
                          className="px-3 py-2 text-xs"
                          onClick={() => {
                            handleClickRow && handleClickRow(item);
                          }}
                        >
                          <span className={getStatusTagClass(item[label])}>
                            {item[label]}
                          </span>
                        </td>
                      );
                    } else if (label.toLowerCase().includes("date") || label === "created_date" || label === "createddate" || label === "createdDate") {
                      const dateValue = item[label] || ((label === "createdDate" || label === "created_date" || label === "createddate") ? (item.createdDate || item.createddate || item.created_date || item.createdOn || item.createdon || item.date) : null);
                      let displayDate = "---";

                      if (typeof dateValue === "string" && dateValue) {
                        const [date] = dateValue.split("T");
                        displayDate = date;
                      }

                      return (
                        <td
                          key={fieldindex}
                          className="px-3 py-2 text-xs font-normal text-slate-500 whitespace-nowrap"
                          onClick={() => {
                            handleClickRow && handleClickRow(item);
                          }}
                        >
                          {displayDate}
                        </td>
                      );
                    } else if (label !== "id" && label !== "select") {
                      return (
                        <td
                          key={fieldindex}
                          className="px-3 py-2 text-xs font-normal text-slate-700"
                          onClick={() => {
                            handleClickRow && handleClickRow(item);
                          }}
                        >
                          {item[label]?.toString()?.charAt(0)?.toUpperCase() +
                            item[label]?.toString()?.slice(1)}
                        </td>
                      );
                    }
                    return null;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListViewUp;